// WebSocket Service for Real-time Updates
// Handles connection, reconnection, and message events

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.userId = null;
    this.isConnecting = false;
    this.shouldReconnect = true;
    this.connectTimeout = null;
  }

  connect(userId, backendUrl) {
    // If already connected to this user, don't reconnect
    if (this.userId === userId && this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected for this user');
      return;
    }
    
    // If WebSocket is currently connecting to same user, wait
    if (this.isConnecting && this.userId === userId) {
      console.log('WebSocket already connecting...');
      return;
    }
    
    // Clear any pending reconnect timeout
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    
    // Close existing connection if switching users or reconnecting
    if (this.ws) {
      this.ws.onclose = null; // Remove handler to prevent reconnect loop
      this.ws.close();
      this.ws = null;
    }

    this.userId = userId;
    this.backendUrl = backendUrl;
    this.isConnecting = true;
    this.shouldReconnect = true;
    
    // Reset reconnect attempts only for new user connection
    if (this.lastUserId !== userId) {
      this.reconnectAttempts = 0;
      this.lastUserId = userId;
    }

    // Convert HTTP URL to WebSocket URL
    const wsUrl = backendUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');

    // Small delay to handle React StrictMode's double mount/unmount
    this.connectTimeout = setTimeout(() => {
      // Check if we still should connect (might have been cancelled)
      if (!this.shouldReconnect || this.userId !== userId) {
        this.isConnecting = false;
        return;
      }
      
      try {
        this.ws = new WebSocket(`${wsUrl}/api/ws/${userId}`);

        this.ws.onopen = () => {
          console.log('✅ WebSocket connected');
          this.reconnectAttempts = 0;
          this.isConnecting = false;
          this.emit('connected', { userId });
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 WebSocket message received:', data.type);
            
            // Emit specific event type
            this.emit(data.type, data);
            
            // Also emit generic 'message' event
            this.emit('message', data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('🔌 WebSocket disconnected:', event.code, event.reason);
          this.isConnecting = false;
          this.emit('disconnected', { code: event.code, reason: event.reason });
          
          // Attempt to reconnect only if allowed and we have connection info
          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts && this.userId && this.backendUrl) {
            this.reconnectAttempts++;
            console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
            this.connectTimeout = setTimeout(() => {
              if (this.shouldReconnect && this.userId && this.backendUrl) {
                this.ws = null;
                this.connect(this.userId, this.backendUrl);
              }
            }, this.reconnectDelay);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          this.emit('error', error);
        };
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        this.isConnecting = false;
      }
    }, 100); // 100ms delay to let React StrictMode settle
  }

  disconnect() {
    // Clear any pending reconnect timeout
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    
    this.shouldReconnect = false; // Prevent auto-reconnect
    this.userId = null;
    this.backendUrl = null;
    
    if (this.ws) {
      this.ws.onclose = null; // Remove handler to prevent reconnect loop
      this.ws.close();
      this.ws = null;
    }
    
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.isConnecting = false;
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket listener for ${event}:`, error);
        }
      });
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

export default wsService;
